import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { PoundSterling, Percent, Users, Info } from 'lucide-react';

// Discount Badge Component for Listing Cards
export const DiscountBadge = ({ business, variant = 'card' }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!business?.discount_type) {
    return null;
  }

  const generateBadgeContent = () => {
    if (business.discount_type === 'flat') {
      if (!business.flat_discount_amount || business.flat_discount_amount <= 0) {
        return null;
      }
      
      return {
        icon: <PoundSterling className="h-3 w-3" />,
        text: `£${business.flat_discount_amount} off all purchases — every customer, every day!`,
        shortText: `£${business.flat_discount_amount} off everyone!`,
        color: 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
      };
    } else if (business.discount_type === 'percentage') {
      const activeDiscounts = Object.entries(business.category_discounts || {})
        .filter(([_, value]) => value && parseInt(value) > 0)
        .map(([category, value]) => ({
          category: formatCategoryName(category),
          value: parseInt(value)
        }));

      if (activeDiscounts.length === 0) {
        return null;
      }

      const maxDiscount = Math.max(...activeDiscounts.map(d => d.value));
      const topCategories = activeDiscounts
        .sort((a, b) => b.value - a.value)
        .slice(0, 2);

      const shortText = activeDiscounts.length === 1 
        ? `${activeDiscounts[0].value}% off ${activeDiscounts[0].category}`
        : activeDiscounts.length === 2
          ? `${topCategories[0].value}% ${topCategories[0].category}, ${topCategories[1].value}% ${topCategories[1].category}`
          : `Up to ${maxDiscount}% off - ${topCategories[0].category}, ${topCategories[1].category} + more`;

      const fullText = `Exclusive savings for ${activeDiscounts.map(d => d.category).join(', ')} — up to ${maxDiscount}% off!`;

      return {
        icon: <Percent className="h-3 w-3" />,
        text: fullText,
        shortText: shortText,
        color: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
        tooltip: activeDiscounts
      };
    }

    return null;
  };

  const formatCategoryName = (category) => {
    const names = {
      students: 'Students',
      pensioners: 'Pensioners',
      government_workers: 'Government',
      nhs_key_workers: 'NHS/Key Workers',
      general_public: 'General Public'
    };
    return names[category] || category.replace(/_/g, ' ');
  };

  const badgeContent = generateBadgeContent();
  
  if (!badgeContent) {
    return null;
  }

  const BadgeComponent = ({ children }) => (
    <Badge 
      className={`${badgeContent.color} border-0 shadow-sm hover:shadow-md transition-all duration-200 ${
        variant === 'card' ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5'
      }`}
    >
      <div className="flex items-center gap-1">
        {badgeContent.icon}
        <span className="font-medium">
          {variant === 'card' && badgeContent.shortText.length > 30 
            ? `Up to ${business.discount_type === 'flat' ? `£${business.flat_discount_amount}` : Math.max(...Object.values(business.category_discounts || {}).filter(v => v).map(v => parseInt(v))) + '%'} off`
            : variant === 'card' ? badgeContent.shortText : badgeContent.text
          }
        </span>
      </div>
    </Badge>
  );

  // If percentage discounts and multiple categories, add tooltip
  if (business.discount_type === 'percentage' && badgeContent.tooltip && badgeContent.tooltip.length > 1) {
    return (
      <TooltipProvider>
        <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
          <TooltipTrigger asChild>
            <div onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
              <BadgeComponent />
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-white border shadow-lg rounded-lg p-3 max-w-xs">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
                <Info className="h-4 w-4" />
                Tap to see full discount breakdown:
              </div>
              {badgeContent.tooltip.map((discount, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-700">{discount.category}:</span>
                  <span className="font-medium text-green-600">{discount.value}%</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <BadgeComponent />;
};

// Full Discount Section for Business Profile Pages
export const DiscountSection = ({ business }) => {
  if (!business?.discount_type) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="text-center text-gray-600">
          <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="font-medium">No active discounts listed yet — check back soon for new offers!</p>
        </div>
      </div>
    );
  }

  const formatCategoryName = (category) => {
    const names = {
      students: 'Students',
      pensioners: 'Pensioners', 
      government_workers: 'Government Staff',
      nhs_key_workers: 'NHS/Key Workers',
      general_public: 'General Public'
    };
    return names[category] || category.replace(/_/g, ' ');
  };

  const renderFlatDiscount = () => (
    <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="bg-orange-500 text-white p-2 rounded-lg">
          <PoundSterling className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-semibold text-orange-800">Available Discounts</h3>
      </div>
      
      <div className="mb-2">
        <p className="text-xl font-bold text-orange-900">
          £{business.flat_discount_amount} off all purchases — every customer, every day!
        </p>
      </div>
      
      <p className="text-sm text-orange-700">
        Show your QR code at checkout to claim your discount.
        {business.minimum_expenditure && ` Minimum spend: £${business.minimum_expenditure}.`}
      </p>
      
      {business.discount_applicable_items && business.discount_applicable_items.length > 0 && 
        business.discount_applicable_items[0] && (
        <div className="mt-3">
          <p className="text-sm text-orange-700 font-medium">Applies to:</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {business.discount_applicable_items.filter(item => item.trim()).slice(0, 5).map((item, index) => (
              <Badge key={index} variant="outline" className="text-xs bg-white border-orange-300 text-orange-700">
                {item}
              </Badge>
            ))}
            {business.discount_applicable_items.filter(item => item.trim()).length > 5 && (
              <Badge variant="outline" className="text-xs bg-white border-orange-300 text-orange-700">
                +{business.discount_applicable_items.filter(item => item.trim()).length - 5} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderPercentageDiscounts = () => {
    const activeDiscounts = Object.entries(business.category_discounts || {})
      .filter(([_, value]) => value && parseInt(value) > 0)
      .map(([category, value]) => ({
        category: formatCategoryName(category),
        value: parseInt(value)
      }))
      .sort((a, b) => b.value - a.value);

    if (activeDiscounts.length === 0) {
      return null;
    }

    return (
      <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-green-500 text-white p-2 rounded-lg">
            <Percent className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-green-800">Exclusive savings for our community!</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
          {activeDiscounts.map((discount, index) => (
            <div key={index} className="flex justify-between items-center bg-white bg-opacity-60 rounded-lg px-3 py-2">
              <span className="font-medium text-green-800">{discount.category}:</span>
              <span className="text-lg font-bold text-green-700">{discount.value}% off</span>
            </div>
          ))}
        </div>
        
        <p className="text-sm text-green-700">
          Discounts applied automatically or by showing your shopper code in-store.
          {business.minimum_expenditure && ` Minimum spend: £${business.minimum_expenditure}.`}
        </p>
        
        {business.discount_applicable_items && business.discount_applicable_items.length > 0 && 
          business.discount_applicable_items[0] && (
          <div className="mt-3">
            <p className="text-sm text-green-700 font-medium">Applies to:</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {business.discount_applicable_items.filter(item => item.trim()).slice(0, 5).map((item, index) => (
                <Badge key={index} variant="outline" className="text-xs bg-white border-green-300 text-green-700">
                  {item}
                </Badge>
              ))}
              {business.discount_applicable_items.filter(item => item.trim()).length > 5 && (
                <Badge variant="outline" className="text-xs bg-white border-green-300 text-green-700">
                  +{business.discount_applicable_items.filter(item => item.trim()).length - 5} more
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-6">
      {business.discount_type === 'flat' ? renderFlatDiscount() : renderPercentageDiscounts()}
    </div>
  );
};

export default { DiscountBadge, DiscountSection };